type APIErrorBody = {
  error?: {
    code?: string;
    message?: string;
    field?: string;
  };
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly field?: string;

  constructor(status: number, code: string, message: string, field?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.field = field;
  }
}

export class AdminApiClient {
  private csrfToken = '';

  constructor(
    private readonly fetcher: typeof fetch = globalThis.fetch.bind(globalThis),
  ) {}

  setCSRFToken(token: string | null | undefined): void {
    this.csrfToken = token ?? '';
  }

  get<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'GET' });
  }

  post<T>(path: string, body?: unknown, headers?: HeadersInit): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
      headers,
    });
  }

  patch<T>(path: string, body: unknown, headers?: HeadersInit): Promise<T> {
    return this.request<T>(path, {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers,
    });
  }

  delete<T>(path: string, headers?: HeadersInit): Promise<T> {
    return this.request<T>(path, { method: 'DELETE', headers });
  }

  async request<T>(path: string, init: RequestInit): Promise<T> {
    const headers = new Headers(init.headers);
    if (init.body !== undefined && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    if (this.csrfToken && init.method && !['GET', 'HEAD', 'OPTIONS'].includes(init.method)) {
      headers.set('X-CSRF-Token', this.csrfToken);
    }

    const response = await this.fetcher(path, {
      ...init,
      credentials: 'same-origin',
      headers: Object.fromEntries(headers.entries()),
    });
    const data = await response.json().catch(() => ({})) as T & APIErrorBody;
    if (!response.ok) {
      throw new ApiError(
        response.status,
        data.error?.code ?? 'request_failed',
        data.error?.message ?? `HTTP ${response.status}`,
        data.error?.field,
      );
    }
    return data;
  }
}

export const adminAPI = new AdminApiClient();
